import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * IndexedDB schema for Bunko.
 *
 * One database (`bunko`), six stores. The credentials store holds
 * encrypted blobs; everything else stores public metadata in plaintext.
 *
 * To add a store: bump `DB_VERSION`, add the type in {@link BunkoDB},
 * and create it in the `upgrade` callback for the new version.
 */

export const DB_NAME = 'bunko';
export const DB_VERSION = 3;

/** Persisted shape of an encrypted credential. */
export interface CredentialRow {
  source: string;
  /** Base64 AES-GCM ciphertext of `{ cookies, extra }` JSON. */
  ciphertext: string;
  /** Base64 96-bit IV unique to this row. */
  iv: string;
  addedAt: number;
  /** "ok" = last validation succeeded; "stale" = surfaced re-auth banner. */
  status: 'ok' | 'stale';
  /** Optional cached username for UI. */
  username?: string;
  /**
   * `'anonymous'` mirrors the encrypted `auth.anonymous` flag so the
   * Settings page and `useLibrary` can branch without decrypting. Default
   * (undefined) means a normal logged-in session.
   */
  mode?: 'anonymous';
}

export interface FavoriteRow {
  /** Compound key: `${source}|${comicId}`. */
  key: string;
  source: string;
  comicId: string;
  /** Effective visible timestamp, kept for legacy indexes and sorting. */
  addedAt: number;
  /** Timestamp for Bunko app-level saves. */
  appSavedAt?: number;
  /** Tombstone timestamp for Bunko app-level unsaves. */
  appUnsavedAt?: number;
  /** Timestamp for source-site/native favorite state. */
  sourceSavedAt?: number;
  /** Timestamp for source-site/native unfavorite state. */
  sourceUnsavedAt?: number;
  /** Last successful source-site favorite refresh touching this row. */
  sourceLastSyncedAt?: number;
  sourceNativeCat?: number;
  titleSnapshot: string;
  coverUrl: string;
  /** Sources may stash extra display state (tags for blacklist, etc.). */
  extra?: Record<string, unknown>;
}

export interface HistoryRow {
  key: string;
  source: string;
  comicId: string;
  readUnitId?: string;
  readUnitTitle?: string;
  readGroupId?: string;
  readGroupTitle?: string;
  lastPage: number;
  totalPages: number;
  lastSeen: number;
  titleSnapshot: string;
  coverUrl: string;
}

export interface BlacklistRow {
  /** Compound key: `${source ?? '*'}|${tag}`. */
  key: string;
  /** `null` source means "applies to all". */
  source: string | null;
  tag: string;
  addedAt: number;
}

export interface PrefsRow {
  key: string;
  value: unknown;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

/**
 * Persisted shape of a saved search. Source-scoped entries pin to one source;
 * `source === '*'` denotes a cross-source aggregate search. The URL is a
 * relative path like `/source/hitomi?q=foo&sort=date` or `/search?q=foo`
 * that round-trips through React Router unchanged.
 */
export interface SavedSearchRow {
  /** Stable id (uuid-ish). Used as the primary key. */
  id: string;
  /** User-supplied short label. */
  name: string;
  /** Source id, or `'*'` for cross-source. */
  source: string;
  /** Relative URL to navigate to (path + query string). */
  url: string;
  createdAt: number;
}

interface BunkoDB extends DBSchema {
  meta: {
    key: string;
    value: MetaRow;
  };
  credentials: {
    key: string;
    value: CredentialRow;
    indexes: { 'by-status': string };
  };
  favorites: {
    key: string;
    value: FavoriteRow;
    indexes: {
      'by-source': string;
      'by-source-addedAt': [string, number];
    };
  };
  history: {
    key: string;
    value: HistoryRow;
    indexes: {
      'by-source': string;
      'by-lastSeen': number;
    };
  };
  blacklist: {
    key: string;
    value: BlacklistRow;
    indexes: { 'by-tag': string };
  };
  prefs: {
    key: string;
    value: PrefsRow;
  };
  savedSearches: {
    key: string;
    value: SavedSearchRow;
    indexes: {
      'by-source': string;
      'by-createdAt': number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<BunkoDB>> | undefined;

/**
 * Open the database lazily and reuse the connection. Callers should never
 * cache the resolved value across reloads.
 */
export function getDb(): Promise<IDBPDatabase<BunkoDB>> {
  if (!dbPromise) {
    dbPromise = openDB<BunkoDB>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          db.createObjectStore('meta', { keyPath: 'key' });

          const creds = db.createObjectStore('credentials', { keyPath: 'source' });
          creds.createIndex('by-status', 'status');

          const favs = db.createObjectStore('favorites', { keyPath: 'key' });
          favs.createIndex('by-source', 'source');
          favs.createIndex('by-source-addedAt', ['source', 'addedAt']);

          const hist = db.createObjectStore('history', { keyPath: 'key' });
          hist.createIndex('by-source', 'source');
          hist.createIndex('by-lastSeen', 'lastSeen');

          const bl = db.createObjectStore('blacklist', { keyPath: 'key' });
          bl.createIndex('by-tag', 'tag');

          db.createObjectStore('prefs', { keyPath: 'key' });
        }
        if (oldVersion < 2) {
          const ss = db.createObjectStore('savedSearches', { keyPath: 'id' });
          ss.createIndex('by-source', 'source');
          ss.createIndex('by-createdAt', 'createdAt');
        }
        if (oldVersion > 0 && oldVersion < 3) {
          const store = transaction.objectStore('favorites');
          let cursor = await store.openCursor();
          while (cursor) {
            const row = cursor.value as FavoriteRow;
            if (
              row.appSavedAt === undefined &&
              row.appUnsavedAt === undefined &&
              row.sourceSavedAt === undefined &&
              row.sourceUnsavedAt === undefined
            ) {
              await cursor.update({ ...row, appSavedAt: row.addedAt });
            }
            cursor = await cursor.continue();
          }
        }
      },
    });
  }
  return dbPromise;
}

/** Compose the favorites/history compound key. */
export function rowKey(source: string, comicId: string): string {
  return `${source}|${comicId}`;
}

export function historyKey(source: string, comicId: string, readUnitId?: string): string {
  return readUnitId ? `${source}|${comicId}|${readUnitId}` : rowKey(source, comicId);
}

/** Compose the blacklist compound key. */
export function blacklistKey(source: string | null, tag: string): string {
  return `${source ?? '*'}|${tag}`;
}

export type DB = IDBPDatabase<BunkoDB>;
